import React, { useCallback, useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { useEditor, useValue } from 'tldraw';
import { useBoardActivities } from '../context/BoardActivitiesContext';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { useBoardStore } from '../store/useBoardStore';
import { getCanvasMessages } from '../utils/canvasI18n';
import { getActivityId } from '../utils/autoLayout';
import { flushBoardPlacements } from '../utils/boardPlacementPersist';
import { startBoardPinPointerInteraction } from '../utils/pinCardDrag';
import {
  epistemicLabelForType,
  isEpistemicLabelShape,
  nextEpistemicType,
  readShapeEpistemicType,
  writeShapeEpistemicType,
} from '../utils/epistemicType';

const LABEL_GAP_SCREEN = 10;
const VIEW_PAD = 48;

function EpistemicLabelChip({
  x,
  y,
  text,
  epistemicType,
  interactive,
  ariaLabel,
  tooltip,
  onPointerDown,
  onActivate,
}) {
  if (!text) return null;

  const className = [
    'board-pin-epistemic-label',
    epistemicType ? `is-${epistemicType}` : 'is-unset',
    interactive ? 'is-interactive' : 'is-readonly',
  ].join(' ');

  // Non-button so pin drag helper can own the gesture (buttons are treated as no-drag controls).
  const chip = interactive ? (
    <span
      role="button"
      tabIndex={0}
      className={className}
      aria-label={ariaLabel}
      onPointerDown={onPointerDown}
      onKeyDown={event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onActivate?.(event);
      }}
    >
      {text}
    </span>
  ) : (
    <span className={className}>{text}</span>
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        left: x,
        top: y,
        transform: 'translate(-50%, -100%)',
        zIndex: 12,
        pointerEvents: interactive ? 'auto' : 'none',
      }}
    >
      {interactive && tooltip ? (
        <Tooltip title={tooltip} arrow placement="top">
          {chip}
        </Tooltip>
      ) : (
        chip
      )}
    </Box>
  );
}

/**
 * Screen-space knowledge-type labels above user pins, pasted text/images, and AI pins.
 * Typed objects always show a label. Unlabeled ones show "+" only while selected.
 * Drag moves the object; click / keyboard cycles knowledge type.
 */
export default function PinEpistemicLabels() {
  const editor = useEditor();
  const { activityById } = useBoardActivities();
  const canEdit = useBoardStore(s => s.canEdit || s.isOwner);
  const objectProcessing = useBoardStore(s => s.objectProcessing);
  const setPinEpistemicType = useBoardStore(s => s.setPinEpistemicType);
  const bumpCanvasEpistemicNonce = useBoardStore(s => s.bumpCanvasEpistemicNonce);
  const { geminiLanguage } = useGeminiLanguage();
  const messages = useMemo(() => getCanvasMessages(geminiLanguage), [geminiLanguage]);

  const allowInteraction = useValue(
    'pin-epistemic-allow-interaction',
    () => Boolean(canEdit && !editor.getIsReadonly() && editor.isIn('select')),
    [editor, canEdit]
  );

  const items = useValue(
    'pin-epistemic-label-items',
    () => {
      editor.getCamera?.();
      const viewport = editor.getViewportScreenBounds?.();
      const selectedIds = new Set(editor.getSelectedShapeIds?.() || []);
      const editingId = editor.getEditingShapeId?.() || null;
      const next = [];

      const processingShapeIds = new Set();
      const processingActivityIds = new Set();
      for (const entry of Object.values(objectProcessing || {})) {
        if (entry?.shapeId) processingShapeIds.add(String(entry.shapeId));
        if (entry?.activityId) processingActivityIds.add(String(entry.activityId));
      }

      for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (!isEpistemicLabelShape(shape)) continue;
        if (shape.id === editingId) continue;
        if (processingShapeIds.has(shape.id)) continue;
        if (
          shape.type === 'pin-card'
          && processingActivityIds.has(String(shape.meta?.activityId || ''))
        ) {
          continue;
        }

        const epistemicType = readShapeEpistemicType(shape, activityById);
        const isSelected = selectedIds.has(shape.id);
        // Typed objects always show. Unlabeled ones only show "+" while selected.
        if (!epistemicType && !(allowInteraction && isSelected && !shape.isLocked)) continue;

        const bounds = editor.getShapePageBounds(shape);
        if (!bounds) continue;

        const screen = editor.pageToScreen({
          x: bounds.x + bounds.w / 2,
          y: bounds.y,
        });

        if (viewport) {
          if (
            screen.x < viewport.x - VIEW_PAD
            || screen.x > viewport.x + viewport.w + VIEW_PAD
            || screen.y < viewport.y - VIEW_PAD
            || screen.y > viewport.y + viewport.h + VIEW_PAD
          ) {
            continue;
          }
        }

        const label = epistemicLabelForType(epistemicType, messages);
        const interactive = allowInteraction && !shape.isLocked;
        next.push({
          id: shape.id,
          shapeType: shape.type,
          activityId: shape.type === 'pin-card' ? String(shape.meta.activityId) : null,
          epistemicType,
          interactive,
          text: label || (interactive && isSelected ? '+' : null),
          x: screen.x,
          y: screen.y - LABEL_GAP_SCREEN,
        });
      }

      return next;
    },
    [editor, allowInteraction, activityById, messages, objectProcessing]
  );

  const handleCycle = useCallback(
    (shapeId, activityId) => {
      if (!allowInteraction || !shapeId) return;
      if (activityId) {
        const id = String(activityId);
        const current = useBoardStore
          .getState()
          .activities
          .find(a => getActivityId(a) === id)
          ?.epistemicType;
        const next = nextEpistemicType(current);
        void setPinEpistemicType(id, next).catch(() => {});
        editor.select?.(shapeId);
        return;
      }

      const shape = editor.getShape(shapeId);
      if (!shape || shape.isLocked) return;
      const next = nextEpistemicType(shape.meta?.epistemicType);
      if (!writeShapeEpistemicType(editor, shape, next)) return;
      bumpCanvasEpistemicNonce?.();
      editor.select?.(shapeId);
    },
    [allowInteraction, editor, setPinEpistemicType, bumpCanvasEpistemicNonce]
  );

  const handleLabelPointerDown = useCallback(
    (event, shapeId, shapeType, activityId) => {
      if (!allowInteraction || !shapeId) return;
      startBoardPinPointerInteraction(editor, event, {
        shapeId,
        shapeType,
        historyLabel: 'epistemic-label',
        allowDrag: true,
        getDragIds: () => {
          const movingIds = editor
            .getSelectedShapeIds()
            .filter(id => isEpistemicLabelShape(editor.getShape(id)));
          return movingIds.includes(shapeId) ? movingIds : [shapeId];
        },
        onClick: () => handleCycle(shapeId, activityId),
        onDrop: () => {
          flushBoardPlacements();
          queueMicrotask(() => flushBoardPlacements());
        },
      });
    },
    [allowInteraction, editor, handleCycle]
  );

  if (!items.length) return null;

  return (
    <>
      {items.map(item => {
        if (!item.text) return null;
        const tipKey = item.epistemicType || 'unset';
        const tooltip = messages.epistemicTooltips?.[tipKey] || messages.epistemicCycleAria;
        return (
          <EpistemicLabelChip
            key={item.id}
            x={item.x}
            y={item.y}
            text={item.text}
            epistemicType={item.epistemicType}
            interactive={item.interactive}
            ariaLabel={messages.epistemicCycleAria}
            tooltip={tooltip}
            onPointerDown={event => (
              handleLabelPointerDown(event, item.id, item.shapeType, item.activityId)
            )}
            onActivate={() => handleCycle(item.id, item.activityId)}
          />
        );
      })}
    </>
  );
}
